import { Box, Grid, GridItem } from "@chakra-ui/layout";
import { Button } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  type CreditAccountItemFormState,
  ExpenseCreditAccountsCardStore,
} from "@src/components/modules/expenses/Create/components/credit-accounts-card/store";
import {
  NumberInput,
  RemoveButton,
  Select,
  type SelectRef,
  TextField,
} from "@src/components/ui-kit";
import { GlobalCreditAccountSelect } from "@src/components/ui-kit/global-credit-account-select";
import { SelectManual } from "@src/components/ui-kit/Select/manual";
import { fieldToSelectProps } from "@src/utils/forms/inputHelpers";
import { useStore as useAppStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { useRef } from "react";
import { useStore } from "../../../store";

type Props = {
  item: CreditAccountItemFormState;
  store: ExpenseCreditAccountsCardStore;
  itemIndex: number;
};
export const CreditAccountItem = observer(function CreditAccountItem({
  item,
  store,
  itemIndex,
}: Props) {
  const appStore = useAppStore();
  const expenseStore = useStore();

  const isNewCreditAccountSelected =
    item.$.global_credit_account_id.value ===
    ExpenseCreditAccountsCardStore.NEW_CREDIT_ACCOUNT_VALUE;
  const descriptionSelectRef = useRef<SelectRef>(null);

  return (
    <Box key={item.$.id.$} mb="6">
      <Grid
        gridTemplateColumns={`repeat(${isNewCreditAccountSelected ? 7 : 6}, 1fr) 32px`}
        gap="3"
        mb="3"
      >
        <GridItem>
          <GlobalCreditAccountSelect
            placeholder={t`Choose credit account`}
            options={store.defaultCreditAccountOptions}
            value={item.$.global_credit_account_id.value}
            onChange={item.$.global_credit_account_id.onChange}
            error={item.$.global_credit_account_id.error}
            ignoreIds={store.ignoreCreditAccountOptionsIds}
          />
        </GridItem>

        <GridItem>
          <NumberInput
            currency
            suffix={expenseStore.selectedCurrency?.code}
            value={item.$.amount_in_currency.value}
            onChange={item.$.amount_in_currency.onChange}
            error={item.$.amount_in_currency.error}
          />
        </GridItem>

        {isNewCreditAccountSelected && (
          <GridItem>
            <TextField
              placeholder={t`Credit account title`}
              value={item.$.global_credit_account_title.value}
              onChange={item.$.global_credit_account_title.onChange}
              error={item.$.global_credit_account_title.error}
            />
          </GridItem>
        )}

        <GridItem>
          <Select
            isDisabled={!expenseStore.canUserAccess("set_accounting")}
            placeholder={t`Expense account`}
            asPortal
            {...fieldToSelectProps(
              item.$.accounting_account_id,
              store.accountingAccountsOptions,
            )}
          />
        </GridItem>
        <GridItem>
          <Select
            asPortal
            placeholder={t`Cost center`}
            options={store.costCentersOptions}
            value={item.$.accounting_centre_id.value}
            onChange={item.$.accounting_centre_id.onChange}
            error={item.$.accounting_centre_id.error}
            isDisabled={!expenseStore.canUserAccess("set_accounting")}
          />
        </GridItem>

        <GridItem>
          <SelectManual
            ref={descriptionSelectRef}
            allowCreateOnBlur
            asPortal
            placeholder={t`Description`}
            options={store.descriptionOptions}
            value={item.$.description_id.value}
            onChange={item.$.description_id.onChange}
            error={item.$.description_id.error}
            noOptionsMessage={() => t`No descriptions`}
            isDisabled={!expenseStore.canUserAccess("set_accounting")}
            onCreateOption={(title) =>
              store.handleCreateDescription(title, itemIndex)
            }
            isClearable
            topExtraContent={
              <Button
                px="2"
                py="2"
                onClick={() => {
                  expenseStore.expenseItemDescriptionModalState.onOpen();
                  descriptionSelectRef.current?.onMenuClose();
                }}
                size="sm"
                variant="link"
              >
                <Trans>Edit descriptions</Trans>
              </Button>
            }
          />
        </GridItem>

        {appStore.workspaceStore.settings
          ?.expense_effectix_custom_cost_ratio && (
          <GridItem>
            <TextField
              placeholder={t`Cost ratio`}
              value={item.$.custom_cost_ratio.value}
              onChange={item.$.custom_cost_ratio.onChange}
              error={item.$.custom_cost_ratio.error}
              isDisabled={!expenseStore.canUserAccess("set_accounting")}
            />
          </GridItem>
        )}

        <GridItem>
          <RemoveButton onClick={() => store.removeItem(itemIndex)} />
        </GridItem>
      </Grid>
    </Box>
  );
});
